import '../IntegrationsTab.scss'
import React, { useMemo, useState } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'

import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { SalesforceSettingsModal } from './SalesforceSettingsModal'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { Link } from 'react-router-dom'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

const ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_SALESFORCE_ADMIN_CONSENT_LINK!

export type SalesforceIntegrationRowProps = { useIntegrationRedirectUri?: boolean }

export const SalesforceIntegrationRow: React.FC<SalesforceIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const hasSalesforceIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.SALESFORCE))
  }, [userData])

  const handleShowSettingsModal = async () => {
    setShowSettingsModal(true)
  }

  const handleHideSettings = () => {
    setShowSettingsModal(false)
  }

  const authorizeSalesforce = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.SALESFORCE)
    const state = useIntegrationRedirectUri ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations') : createState()
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.SALESFORCE}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallSalesforce = () => {
    const user = userData
    if (!user || !user.integrations) {
      return
    }
    const integration = user.integrations.find((i) => i.serviceId === IntegrationServiceId.SALESFORCE)
    if (integration) {
      deleteUserIntegration({
        accountId,
        payload: {
          userId: user.id,
          integrationId: integration.id,
        },
      })
    }
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      {showSettingsModal && <SalesforceSettingsModal show={showSettingsModal} onHide={handleHideSettings} />}

      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.SALESFORCE} width={48} height={48} />
            </div>
            <div>
              <h6>Salesforce</h6>
              <div>Connect your Zocks account to Salesforce.</div>
              <div>
                Your Salesforce Admin must enable the Zocks app in Salesforce. For more info, click{' '}
                <Link className='zocks-text-gray-900 text-decoration-underline' to={ADMIN_CONSENT_LINK}>
                  here.
                </Link>{' '}
                &nbsp;&nbsp;
                <Tooltip
                  className='zocks-text-gray-500'
                  placement='right'
                  title='Go to Apps > Connected Apps > Connected Apps OAuth Usage and click Install next to Zocks before connecting.'
                >
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            <div className='d-flex gap-3'>
              <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:read', superScope: ['account:admin'] }}>
                <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                  <i className='bi bi-gear' />
                </Button>
              </ScopedBlock>
            </div>
            <div className='connect-button-container'>
              {hasSalesforceIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallSalesforce} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={authorizeSalesforce}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
